import {useEffect} from 'react'
import {AlertIcon, ClockIcon, PulseIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {RulesetEnforcement} from '@github-ui/repos-rules-types/rules-types'
import {NewRulesetButton} from '../NewRulesetButton'
import type {FlashAlert} from '@github-ui/dismissible-flash'

import styles from './InsightsBlank.module.css'

const UNAVAILABLE_HEADING = "Rule insights couldn't be loaded"
const UNAVAILABLE_DESCRIPTION = 'Something went wrong while loading rule evaluations. Try reloading in a few moments.'

export function InsightsBlank({
  showCreateButton,
  timedOut,
  unavailable,
  setFlashAlert,
}: {
  showCreateButton: boolean
  timedOut?: boolean
  unavailable?: boolean
  setFlashAlert: (flashAlert: FlashAlert) => void
}) {
  const {resolvePath} = useRelativeNavigation()

  if (unavailable) {
    return <InsightsUnavailableBlank />
  }

  const IconComponent = timedOut ? ClockIcon : PulseIcon

  return (
    <div className="blankslate">
      <IconComponent size={24} />
      <h2 className="blankslate-heading">
        {timedOut ? 'Rule insights search timed out' : 'No rule evaluations matched your search'}
      </h2>
      {timedOut ? (
        <p>Try adjusting your filters or reducing the time range of your search</p>
      ) : showCreateButton ? (
        <>
          <p>Try expanding your search or creating a new ruleset in evaluate mode</p>
          <NewRulesetButton
            rulesetsUrl={resolvePath('../')}
            reloadDocument
            defaultEnforcement={RulesetEnforcement.Evaluate}
            setFlashAlert={setFlashAlert}
            className={styles.NewRulesetButton}
          />
        </>
      ) : null}
    </div>
  )
}

// Rendered only when insights failed to load. The blankslate swaps in without moving
// focus, so mounting it announces the failure.
function InsightsUnavailableBlank() {
  useEffect(() => {
    announce(`${UNAVAILABLE_HEADING}. ${UNAVAILABLE_DESCRIPTION}`)
  }, [])

  return (
    <div className="blankslate">
      <AlertIcon size={24} />
      <h2 className="blankslate-heading">{UNAVAILABLE_HEADING}</h2>
      <p>{UNAVAILABLE_DESCRIPTION}</p>
    </div>
  )
}
